import React, { useState, useEffect } from 'react';
import { Link as RouterLink, useNavigate, useLocation } from 'react-router-dom';
import { signInWithEmailAndPassword } from 'firebase/auth';
import { auth } from '../lib/firebase';
import { signInWithGoogleAccount } from '@/utils/googleSignIn';
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { AlertCircle, Eye, EyeOff, LogIn } from "lucide-react";
import { Alert, AlertDescription } from "../components/ui/alert";
import LandingNavbar from "@/components/LandingNavbar";
import IndexMark from '@/components/index/IndexMark';
import { homePathForCurrentUser } from '@/hooks/useUserRoles';

// interface for the location state
interface LocationState {
  from?: string;
}

/**
 * Login page component
 * 
 * @returns React component
 */
const Login: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  
  // Get intended destination from location state
  const state = location.state as LocationState;
  const from = state?.from || '/home';
  
  // Redirect if already authenticated
  useEffect(() => {
    if (!auth.currentUser) return;
    void homePathForCurrentUser().then((path) => {
      navigate(from && from !== '/home' ? from : path, { replace: true });
    });
  }, [navigate, from]);
  
  // Form state
  const [formData, setFormData] = useState({
    email: '',
    password: '',
  });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Handle input change
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };
  
  // Toggle password visibility
  const togglePasswordVisibility = () => {
    setShowPassword(prev => !prev);
  };
  
  // Handle form submission
  const handleSubmit = async (e: { preventDefault: () => void }) => {
    e.preventDefault();
    
    if (!formData.email || !formData.password) {
      return;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      await signInWithEmailAndPassword(auth, formData.email, formData.password);
      navigate(await homePathForCurrentUser(), { replace: true });
    } catch (err: any) {
      console.error('Login error:', err);
      // Map Firebase error codes to user-friendly messages
      let errorMessage = 'Login failed. Please try again.';
      if (err.code === 'auth/invalid-email') {
        errorMessage = 'Invalid email address.';
      } else if (err.code === 'auth/user-not-found' || err.code === 'auth/wrong-password') {
        errorMessage = 'Invalid email or password.';
      } else if (err.code === 'auth/user-disabled') {
        errorMessage = 'This account has been disabled.';
      } else if (err.code === 'auth/too-many-requests') {
        errorMessage = 'Too many failed attempts. Please try again later.';
      } else if (err.code === 'auth/operation-not-allowed') {
        errorMessage = 'This sign-in method is not enabled. Try Google, or create an email/password account.';
      } else if (err.message) {
        errorMessage = err.message;
      }
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };
  
  const handleGoogleLogin = async () => {
    setLoading(true);
    setError(null);
    try {
      await signInWithGoogleAccount();
      navigate(await homePathForCurrentUser(), { replace: true });
    } catch (err: any) {
      console.error('Google login error:', err);
      if (err?.code === 'auth/popup-closed-by-user' || err?.code === 'auth/cancelled-popup-request') {
        setError(null);
      } else if (err?.code === 'auth/operation-not-allowed') {
        setError('Google sign-in is not enabled for this project yet.');
      } else if (err?.code === 'auth/account-exists-with-different-credential') {
        setError('An account already exists with this email using a different sign-in method.');
      } else {
        setError(err?.message || 'Login with Google failed. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };
  
  return (
    <div className="min-h-screen flex flex-col bg-beige">
      <LandingNavbar />
      
      <main className="flex-1 flex items-center justify-center pt-24 pb-10 px-4">
        <div className="w-full max-w-md">
          <div className="bg-beige-dark rounded-sm p-8 border border-border-light shadow-sm">
            <div className="flex flex-col items-center text-center mb-6">
              <div className="mb-4">
                <IndexMark theme="light" />
                <p className="text-[10px] font-mono uppercase tracking-wider text-text-dark/50 mt-2">
                  Forensic screenplay intelligence
                </p>
              </div>
              <h2 className="text-2xl font-sans font-bold text-text-dark tracking-tight">Workspace sign in</h2>
              <p className="mt-2 text-sm text-text-dark opacity-70 font-sans">
                Access your development mandates and Nuclius Index reports
              </p>
            </div>
            
            {/* Error message */}
            {error && (
              <Alert variant="destructive" className="mb-6">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}
            
            {/* Login form */}
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <label htmlFor="email" className="text-sm font-medium text-text-dark font-sans">
                  Email Address
                </label>
                <Input
                  id="email"
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  autoComplete="email"
                  autoFocus
                  className="w-full bg-white border-border-light text-text-dark"
                />
              </div>
              
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <label htmlFor="password" className="text-sm font-medium text-text-dark font-sans">
                    Password
                  </label>
                  <RouterLink
                    to="/forgot-password"
                    className="text-xs text-gold-button hover:underline font-medium font-sans"
                  >
                    Forgot password?
                  </RouterLink>
                </div>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    name="password"
                    value={formData.password}
                    onChange={handleChange}
                    required
                    autoComplete="current-password"
                    className="w-full pr-10 bg-white border-border-light text-text-dark"
                  />
                  <button
                    type="button"
                    onClick={togglePasswordVisibility}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-text-dark opacity-70 hover:opacity-100"
                  >
                    {showPassword ? (
                      <EyeOff className="h-4 w-4" />
                    ) : (
                      <Eye className="h-4 w-4" />
                    )}
                  </button>
                </div>
              </div>
              
              <Button
                type="submit"
                className="w-full py-2 mt-2 bg-gold-button text-white hover:bg-gold-button-dark font-sans"
                disabled={loading}
              >
                {loading ? (
                  <div className="flex items-center justify-center">
                    <div className="w-5 h-5 border-2 border-t-transparent border-white rounded-full animate-spin" />
                  </div>
                ) : (
                  <span className="flex items-center justify-center">
                    Sign In
                    <LogIn className="ml-2 h-4 w-4" />
                  </span>
                )}
              </Button>
              
              <div className="mt-4 text-center">
                <p className="text-sm text-text-dark opacity-70 font-sans">
                  Don't have an account?{' '}
                  <RouterLink
                    to="/register"
                    className="text-gold-button hover:underline font-medium font-sans"
                  >
                    Sign up
                  </RouterLink>
                </p>
              </div>
            </form>
            
            <div className="relative mt-6 mb-6">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-border-light"></div>
              </div>
              <div className="relative flex justify-center text-xs">
                <span className="bg-beige-dark px-2 text-text-dark opacity-70 font-sans">OR</span>
              </div>
            </div>
            
            {/* Social login buttons */}
            <div className="grid gap-3">
              <Button
                type="button"
                variant="outline"
                className="w-full border border-border-light bg-neutral-800 text-white hover:bg-gold-button hover:text-white hover:border-gold-button font-sans transition-colors"
                onClick={() => void handleGoogleLogin()}
                disabled={loading}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-4 w-4 mr-2"
                  viewBox="0 0 48 48"
                >
                  <path
                    fill="#FFC107"
                    d="M43.611,20.083H42V20H24v8h11.303c-1.649,4.657-6.08,8-11.303,8c-6.627,0-12-5.373-12-12c0-6.627,5.373-12,12-12c3.059,0,5.842,1.154,7.961,3.039l5.657-5.657C34.046,6.053,29.268,4,24,4C12.955,4,4,12.955,4,24c0,11.045,8.955,20,20,20c11.045,0,20-8.955,20-20C44,22.659,43.862,21.35,43.611,20.083z"
                  />
                  <path
                    fill="#FF3D00"
                    d="M6.306,14.691l6.571,4.819C14.655,15.108,18.961,12,24,12c3.059,0,5.842,1.154,7.961,3.039l5.657-5.657C34.046,6.053,29.268,4,24,4C16.318,4,9.656,8.337,6.306,14.691z"
                  />
                  <path
                    fill="#4CAF50"
                    d="M24,44c5.166,0,9.86-1.977,13.409-5.192l-6.19-5.238C29.211,35.091,26.715,36,24,36c-5.202,0-9.619-3.317-11.283-7.946l-6.522,5.025C9.505,39.556,16.227,44,24,44z"
                  />
                  <path
                    fill="#1976D2"
                    d="M43.611,20.083H42V20H24v8h11.303c-0.792,2.237-2.231,4.166-4.087,5.571c0.001-0.001,0.002-0.001,0.003-0.002l6.19,5.238C36.971,39.205,44,34,44,24C44,22.659,43.862,21.35,43.611,20.083z"
                  />
                </svg>
                Sign in with Google
              </Button>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default Login; 